import { useState } from 'react'
import NewEliteEliteInsightsTiles from '../components/NewEliteEliteInsightsTiles'
import NewEliteLandingKeyFeaturesCarousel from '../components/NewEliteLandingKeyFeaturesCarousel'
import NewEliteLandingPageBenefits from '../components/NewEliteLandingPageBenefits'
import NewEliteLandingPlanSelector from '../components/NewEliteLandingPlanSelector'
import NewEliteLandingTestimonialsCarousel from '../components/NewEliteLandingTestimonialsCarousel'
import NewEliteLandingTrustStatement from '../components/NewEliteLandingTrustStatement'
import { EliteLandingWebviewBackBar } from '../components/EliteLandingWebviewBackBar'
import { ELiteMembershipStatus } from '../../../types/mutual-fund-detail.types'
import {
  EliteLandingPageState,
  type EliteOnboardingStatusResponse,
  type PortfolioInsightsLockedInfo
} from '../../../types/misc.types'
import { getTopPadding } from '../../../utils/sessionStorage'
import { useEliteOnboardingAdvisoryStatus } from '../hooks/useEliteOnboardingAdvisoryStatus'
import { getEliteBuyCtaEventExtras } from '../../../utils/eliteEventExtras'
import { EliteBuyCtaExtrasProvider } from '../../../components/elite/EliteBuyCtaExtrasContext'
import { useSyncEliteLandingPlanFromEligiblePlans } from '../hooks/useSyncEliteLandingPlanFromEligiblePlans'
import { getPlanSelectorEliteLandingPageState } from '../eliteLandingPlanSelectorState'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import RightLeafSVG from '../../../icons/RightLeafSVG'
import LeftLeafSVG from '../../../icons/LeftLeafSVG'
import RenderHTML from '../../../components/ui-components/RenderHTML'
import { EliteMembershipCurrentPlanCard } from '../../../components/elite/dashboard/EliteMembershipCurrentPlanCard'
import { EliteMembershipRenewalSection } from '../../../components/elite/dashboard/EliteMembershipRenewalSection'
import { EliteMembershipRenewalStatusCard } from '../../../components/elite/dashboard/EliteMembershipRenewalStatusCard'
import { EliteMembershipUpgradeSection } from '../../../components/elite/dashboard/EliteMembershipUpgradeSection'
import {
  getEliteRenewalHeadlineFirstLine,
  getEliteSubscriptionValidityDateLabel,
  isEliteAutoPayActive,
  shouldShowElitePortfolioInsightsLockBanner,
  shouldShowEliteRenewalBenefitsHeadline,
  shouldShowEliteRenewalPrompt
} from '../../../components/elite/dashboard/eliteMembershipRenewalWindow'
import {
  getEliteCurrentPlanTenure,
  getEliteFocusEligiblePlan,
  getElitePlanStatusLabel,
  getElitePlanTenureLabel,
  getEliteSubscriptionPlanPriceDisplayExGst
} from '../../../components/elite/dashboard/eliteOnboardingFocusPlan'
import type { EliteLandingPlan } from '../planTypes'
import { NewEliteDashboardHeadCardSkeleton } from '../components/NewEliteDashboardHeadCardSkeleton'
import { NewEliteEliteInsightsTilesSkeleton } from '../components/NewEliteEliteInsightsTilesSkeleton'
import NewEliteFamilyPrimaryNonEliteOfferCard, {
  EliteFamilyOfferCardIntent
} from '../components/family/NewEliteFamilyPrimaryNonEliteOfferCard'
import {
  defaultNonFamilyOnboardingViewProps,
  type FamilyOnboardingViewProps
} from './family/familyOnboardingViewProps'
import {
  NewEliteLandingAnalyticsProvider,
  useTrackNewEliteLandingScreenLoadWhenReady
} from '../analytics/NewEliteLandingAnalyticsContext'
import {
  NewEliteLandingAnalyticsScreen,
  type NewEliteLandingAnalyticsScreenType
} from '../analytics/newEliteLandingAnalytics'
import NewEliteLandingFAQs from '../components/NewEliteLandingFAQs'
import { useStickyVisibilityBySentinel } from '../hooks/useStickyVisibilityBySentinel'
import RestrictedAccessBanner from '../components/RestrictedAccessBanner'
import LockDownCounter from '../components/LockDownCounter'
import {
  EliteReferralInviteBanner,
  shouldShowEliteReferralInviteBanner
} from '../../../components/elite/EliteReferralInviteBanner'

const EliteSvg = `
<svg width="42" height="15" viewBox="0 0 42 15" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M-0.000214144 14.3153V0.553628H10.3211V2.84725H2.84704V5.89222H9.92562V8.18584H2.84704V11.9822H10.4002V14.3153H-0.000214144ZM12.8376 14.3153V-4.56072e-06H15.3685V14.3153H12.8376ZM18.2966 14.3153V4.50814H20.8275V14.3153H18.2966ZM19.3445 2.92634C18.9359 2.92634 18.5932 2.79452 18.3164 2.53089C18.0527 2.25407 17.9209 1.91134 17.9209 1.50271V1.42362C17.9209 1.01499 18.0527 0.678854 18.3164 0.41522C18.5932 0.138404 18.9359 -4.56072e-06 19.3445 -4.56072e-06H19.7795C20.1882 -4.56072e-06 20.5243 0.138404 20.7879 0.41522C21.0647 0.678854 21.2031 1.01499 21.2031 1.42362V1.50271C21.2031 1.89816 21.0647 2.2343 20.7879 2.51111C20.5243 2.78793 20.1882 2.92634 19.7795 2.92634H19.3445ZM24.2795 10.4992V6.56449H22.5593V4.50814H24.3586V1.52248H26.7511V4.50814H29.5983V6.56449H26.8104V10.4992H24.2795ZM28.3724 14.5526C26.9884 14.5526 25.9602 14.1901 25.2879 13.4651C24.6156 12.7401 24.2795 11.8108 24.2795 10.6772V10.4992H26.8104V10.7365C26.8104 11.277 26.9752 11.6922 27.3047 11.9822C27.6343 12.259 28.1022 12.3974 28.7086 12.3974C29.1567 12.3974 29.6445 12.3315 30.1717 12.1997V14.3153C29.5917 14.4735 28.992 14.5526 28.3724 14.5526ZM30.7753 9.39197C30.7753 8.33743 30.9928 7.42789 31.4278 6.66335C31.876 5.88563 32.5087 5.29245 33.326 4.88382C34.1432 4.47519 35.0923 4.27087 36.1732 4.27087C37.32 4.27087 38.2823 4.49496 39.06 4.94314C39.8377 5.39132 40.4177 6.00427 40.8 6.78199C41.1823 7.54653 41.3734 8.40334 41.3734 9.35242C41.3734 9.57651 41.3602 9.84674 41.3339 10.1631H32.0803L33.4248 9.07561C33.2535 10.2224 33.4116 11.0924 33.8994 11.6856C34.4003 12.2656 35.1516 12.5556 36.1534 12.5556C36.7993 12.5556 37.3332 12.4304 37.755 12.1799C38.19 11.9294 38.4932 11.6131 38.6646 11.2308H41.2745C41.0373 12.259 40.4902 13.0697 39.6334 13.6628C38.7766 14.256 37.6364 14.5526 36.2128 14.5526C34.5123 14.5526 33.181 14.111 32.2187 13.2278C31.2564 12.3315 30.7753 11.0529 30.7753 9.39197ZM33.3655 9.43151L32.0803 8.5022H38.8623C38.8623 7.82993 38.6316 7.2763 38.1702 6.84131C37.7089 6.39313 37.0432 6.16904 36.1732 6.16904C35.1714 6.16904 34.4266 6.47222 33.9389 7.07858C33.4512 7.68494 33.2601 8.46925 33.3655 9.43151Z" fill="#BB4F22"/>
</svg>
`


function NewEliteDashboardPageContent({
  familyConfig = defaultNonFamilyOnboardingViewProps
}: {
  familyConfig: FamilyOnboardingViewProps
}) {
  const [selectedPlan, setSelectedPlan] = useState<EliteLandingPlan>('yearly')
  const { isStickyVisible: isBackButtonStickyVisible, sentinelRef } = useStickyVisibilityBySentinel()
  const { data, isLoading } = useEliteOnboardingAdvisoryStatus('NEW_ELITE_DASHBOARD_ONBOARDING_ADVISORY_STATUS')
  const eligiblePlans = data?.eligiblePlans ?? []
  const isPlanExpired =
    data?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED && !isEliteAutoPayActive(data)
  const isPortfolioInsightsLocked = data?.portfolioInsightsLockedInfo?.status === 'LOCKED'
  const isEliteMember = data?.subscriptionDetails?.status === ELiteMembershipStatus.ELITE
  const planSelectorLandingState = getPlanSelectorEliteLandingPageState(data?.eliteLandingPageState)

  useSyncEliteLandingPlanFromEligiblePlans(eligiblePlans, setSelectedPlan, data?.selectedPlanDetails)
  useTrackNewEliteLandingScreenLoadWhenReady(!isLoading, data?.eliteLandingPageState, getEliteBuyCtaEventExtras(data))

  return (
    <EliteBuyCtaExtrasProvider status={data}>
      <div
        style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
        className='h-screen overflow-y-auto no-scrollbar overflow-x-hidden overscroll-none bg-[#FFF2DF] z-0 relative'
      >
        <div className='absolute -top-16 left-0 z-[-1] w-full'>
          <img src='/images/new-elite/new-elite-bg.svg' alt='elite-bg' className='w-full h-auto object-cover' />
        </div>
        <EliteLandingWebviewBackBar isStickyVisible={isBackButtonStickyVisible} />
        {isLoading ? (
          <div className='px-0 pt-2'>
            <EliteDashboardHeadCard familyConfig={familyConfig} isStatusLoading eliteOnboardingStatus={data} />
            <div className='w-full'>
              <NewEliteLandingPlanSelector
                familyConfig={familyConfig}
                selectedPlan={selectedPlan}
                onPlanChange={setSelectedPlan}
                eligiblePlans={[]}
                isStatusLoading
              />
            </div>
          </div>
        ) : isPlanExpired ? (
          <div className='px-0 pt-2'>
            <EliteDashboardHeadCard familyConfig={familyConfig} eliteOnboardingStatus={data} />
            {!shouldShowEliteRenewalPrompt(data) ? (
              <div className='w-full'>
                <NewEliteLandingPlanSelector
                  familyConfig={familyConfig}
                  selectedPlan={selectedPlan}
                  onPlanChange={setSelectedPlan}
                  eligiblePlans={eligiblePlans}
                  cta={data?.cta}
                  eliteLandingPageState={data?.eliteLandingPageState}
                  selectedPlanDetails={data?.selectedPlanDetails}
                  activationExpiryDate={data?.subscriptionDetails?.activationExpiryDate}
                  autoPayStatus={data?.subscriptionDetails?.autoPayStatus}
                  renewalStatus={data?.renewal?.status}
                  consentText={data?.consentText}
                  consentTextList={data?.consentTextList}
                  isStatusLoading={isLoading}
                  referralReward={data?.referralReward}
                />
              </div>
            ) : null}
          </div>
        ) : isEliteMember && data ? (
          <>
            <EliteDashboardHeadCard familyConfig={familyConfig} eliteOnboardingStatus={data} />
          </>
        ) : (
          <div>
            <EliteDashboardHeadCard familyConfig={familyConfig} eliteOnboardingStatus={data} />
            {!shouldShowEliteRenewalPrompt(data) ? (
              <div className='w-full'>
                <NewEliteLandingPlanSelector
                  familyConfig={familyConfig}
                  selectedPlan={selectedPlan}
                  onPlanChange={setSelectedPlan}
                  eligiblePlans={eligiblePlans}
                  cta={data?.cta}
                  eliteLandingPageState={planSelectorLandingState}
                  selectedPlanDetails={data?.selectedPlanDetails}
                  activationExpiryDate={data?.subscriptionDetails?.activationExpiryDate}
                  autoPayStatus={data?.subscriptionDetails?.autoPayStatus}
                  renewalStatus={data?.renewal?.status}
                  consentText={data?.consentText}
                  consentTextList={data?.consentTextList}
                  isStatusLoading={isLoading}
                  referralReward={data?.referralReward}
                />
              </div>
            ) : null}
          </div>
        )}
        <div ref={sentinelRef} />

        {isPlanExpired ? (
          <NewEliteLandingPageBenefits
            topContent={
              shouldShowEliteReferralInviteBanner(data, { isFamilyMode: familyConfig.isFamilyMode }) ? (
                <div className='mx-5 pt-6'>
                  <EliteReferralInviteBanner screenName={NewEliteLandingAnalyticsScreen.Dashboard} />
                </div>
              ) : null
            }
          />
        ) : (
          <NewEliteLandingPageBenefits
            heading='Benefits unlocked'
            eliteOnly
            topContent={
              shouldShowEliteReferralInviteBanner(data, { isFamilyMode: familyConfig.isFamilyMode }) ? (
                <div className='mx-5 pt-6'>
                  <EliteReferralInviteBanner screenName={NewEliteLandingAnalyticsScreen.Dashboard} />
                </div>
              ) : null
            }
          />
        )}
        <div className='relative z-0 bg-white px-5 pt-20'>
          <NewEliteLandingKeyFeaturesCarousel selectedPlan={selectedPlan} />
          {!isPortfolioInsightsLocked ? (
            isLoading ? (
              <NewEliteEliteInsightsTilesSkeleton />
            ) : data?.eliteInsights ? (
              <div className='mt-2 w-full'>
                <NewEliteEliteInsightsTiles
                  eliteInsights={data?.eliteInsights}
                  numberOfFundsToRebalance={data?.numberOfFundsToRebalance ?? 0}
                />
              </div>
            ) : null
          ) : null}
          <NewEliteLandingTestimonialsCarousel />
        </div>
        <NewEliteLandingFAQs isFamilyMode={familyConfig.isFamilyMode} />
        <NewEliteLandingTrustStatement />
      </div>
    </EliteBuyCtaExtrasProvider>
  )
}

export default function NewEliteDashboardPage({
  familyConfig = defaultNonFamilyOnboardingViewProps,
  analyticsScreen = NewEliteLandingAnalyticsScreen.Dashboard
}: {
  familyConfig?: FamilyOnboardingViewProps
  analyticsScreen?: NewEliteLandingAnalyticsScreenType
}) {
  return (
    <NewEliteLandingAnalyticsProvider screen={analyticsScreen}>
      <NewEliteDashboardPageContent familyConfig={familyConfig} />
    </NewEliteLandingAnalyticsProvider>
  )
}

export function EliteDashboardHeadCard({
  familyConfig,
  eliteOnboardingStatus,
  isStatusLoading = false
}: {
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
  familyConfig?: FamilyOnboardingViewProps
  isStatusLoading?: boolean
}) {
  const showRenewalPrompt = shouldShowEliteRenewalPrompt(eliteOnboardingStatus)
  const hasUpgradePlan = Boolean(eliteOnboardingStatus?.subscriptionDetails?.upgradeDetails?.upgradePlanDetails)
  const activePlan = getEliteFocusEligiblePlan(eliteOnboardingStatus)
  const { period: planTenurePeriod, value: planTenureValue } = getEliteCurrentPlanTenure(eliteOnboardingStatus)
  const planTenureLabel = getElitePlanTenureLabel({ period: planTenurePeriod, value: planTenureValue })
  const planTypeLabel = `Elite ${planTenureLabel}`
  const planDurationSuffix = planTenureLabel === 'Yearly' ? '/yr' : '/mo'
  const planStatusLabel = getElitePlanStatusLabel(eliteOnboardingStatus)
  const priceDisplay = getEliteSubscriptionPlanPriceDisplayExGst(
    eliteOnboardingStatus?.subscriptionDetails,
    activePlan
  )
  const primaryPlanFees = priceDisplay.primaryExGst
  const basePlanFeesAmount = priceDisplay.listStrikeExGst
  const hasEffectivePlanFees = priceDisplay.showStrikeThrough
  const subscriptionExpiresOn = eliteOnboardingStatus?.subscriptionDetails?.subscriptionExpiresOn
  const isAutoPayActive = isEliteAutoPayActive(eliteOnboardingStatus)
  const validityDateLabel = getEliteSubscriptionValidityDateLabel(eliteOnboardingStatus)
  const isPlanExpired =
    eliteOnboardingStatus?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED &&
    !isAutoPayActive
  const showPortfolioLockBanner =
    isPlanExpired && shouldShowElitePortfolioInsightsLockBanner(eliteOnboardingStatus)
  const showRenewalBenefitsHeadline = shouldShowEliteRenewalBenefitsHeadline(eliteOnboardingStatus)
  return (
    <div>
      {showPortfolioLockBanner ? (
        <ExpiredPortfolioInsightsBanner
          portfolioInsightsLockedInfo={eliteOnboardingStatus?.portfolioInsightsLockedInfo}
        />
      ) : null}
      {!showPortfolioLockBanner ? (
        <div className="flex flex-col justify-center items-center gap-4">
          <div className="flex flex-row justify-center">
            <LeftLeafSVG color="#BB4F2266" />
            <span className="text-[#BB4F22] font-[600] text-base pt-[2px]"><RenderHTML innerHTMLText={EliteSvg} /></span>
            <RightLeafSVG color="#BB4F2266" />
          </div>
          <div>
            <p className="text-[#65321D] text-[28px] font-[500] leading-[1.2] text-center">
              {getEliteRenewalHeadlineFirstLine(eliteOnboardingStatus)}
            </p>
            <p className="font-ibm-plex-serif text-[#65321D] text-[28px] font-medium leading-[1.2] italic text-center">
              {showRenewalBenefitsHeadline ? 'your Elite Benefits' : 'the top 1% with Elite'}
            </p>
          </div>
        </div>
      ) : null}
      <div>
        {isStatusLoading ? (
          <NewEliteDashboardHeadCardSkeleton />
        ) : showRenewalPrompt ? (
          <>
            <div className='flex flex-col gap-4 mx-5'>
              <EliteMembershipRenewalStatusCard eliteOnboardingStatus={eliteOnboardingStatus} />
            </div>
            {familyConfig?.isEliteBlockedBecausePrimaryNotElite ? (
              <NewEliteFamilyPrimaryNonEliteOfferCard
                familyMemberDisplayName={familyConfig.secondaryUserName}
                eligiblePlans={eliteOnboardingStatus?.eligiblePlans ?? []}
                eliteIntent={EliteFamilyOfferCardIntent.Renew}
              />
            ) : (
              <EliteMembershipRenewalSection
                eliteOnboardingStatus={eliteOnboardingStatus}
                familyConfig={familyConfig}
              />
            )}
          </>
        ) : hasUpgradePlan ? (
          <div className='my-10 flex flex-col gap-4 mx-5'>
            <EliteMembershipCurrentPlanCard eliteOnboardingStatus={eliteOnboardingStatus} />
            <EliteMembershipUpgradeSection eliteOnboardingStatus={eliteOnboardingStatus} familyConfig={familyConfig} />
          </div>
        ) : eliteOnboardingStatus?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED &&
          !isAutoPayActive ? (
          <div className='my-10 mx-5'>
            <EliteMembershipRenewalStatusCard eliteOnboardingStatus={eliteOnboardingStatus} />
          </div>
        ) :
          (
            <div className='rounded-2xl bg-white my-10 mx-5'>
              <div className='flex items-start justify-between gap-3 px-5 py-4'>
                <div className=''>
                  <p className='text-sm font-medium text-[#65321D]'>{planTypeLabel}</p>
                  <p className='mt-1 text-[13px] font-medium text-[#BB4F22]'>{planStatusLabel}</p>
                </div>
                <div className='text-right'>
                  <p className='text-sm font-medium text-[#232323]'>
                    {roundOffAndFormatNumberWithCurrency(primaryPlanFees)}
                    {planDurationSuffix}
                    <span className='text-[12px] font-normal text-[#8E9393]'>+GST</span>
                  </p>
                  {hasEffectivePlanFees ? (
                    <p className='text-[13px] text-[#8E9393] line-through'>
                      {roundOffAndFormatNumberWithCurrency(basePlanFeesAmount)}
                      {planDurationSuffix}
                    </p>
                  ) : null}
                </div>
              </div>
              <hr />
              <div className='text-13 px-5 py-4'>
                {validityDateLabel}{' '}
                {subscriptionExpiresOn ? convertEpochToDate(subscriptionExpiresOn, '%d %b %Y') : 'N/A'}
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

export function ExpiredPortfolioInsightsBanner({
  portfolioInsightsLockedInfo
}: {
  portfolioInsightsLockedInfo?: PortfolioInsightsLockedInfo
}) {
  const status = portfolioInsightsLockedInfo?.status

  if (status === 'LOCKED') {
    return <RestrictedAccessBanner />
  }

  if (status === 'GRACE_PERIOD') {
    return (
      <LockDownCounter gracePeriodEndTimestamp={portfolioInsightsLockedInfo?.gracePeriodEndTimestamp} />
    )
  }
  return null

}